import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '@/api/admin'
import { useAuthStore } from '@/stores/authStore'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatDate, formatRelativeDate } from '@/lib/utils'
import { UserX, Eye } from 'lucide-react'

export default function AdminUsers() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { startImpersonation } = useAuthStore()

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: adminApi.listUsers,
  })

  const deactivateMutation = useMutation({
    mutationFn: adminApi.deactivateUser,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  })

  const impersonateMutation = useMutation({
    mutationFn: adminApi.impersonate,
    onSuccess: (data: { access_token: string; refresh_token: string }) => {
      startImpersonation(data.access_token, data.refresh_token)
      queryClient.clear()
      navigate('/dashboard')
    },
  })

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Users</h1>
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Name</th>
                <th className="text-left p-3 font-medium">Email</th>
                <th className="text-left p-3 font-medium">Status</th>
                <th className="text-left p-3 font-medium">Joined</th>
                <th className="text-left p-3 font-medium">Last Login</th>
                <th className="text-left p-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user: { id: string; full_name: string; email: string; is_active: boolean; is_superadmin: boolean; created_at: string; last_login_at: string | null }) => (
                <tr key={user.id} className="border-t hover:bg-muted/20">
                  <td className="p-3">
                    {user.full_name || '—'}
                    {user.is_superadmin && (
                      <Badge variant="destructive" className="ml-2 text-xs">Admin</Badge>
                    )}
                  </td>
                  <td className="p-3">{user.email}</td>
                  <td className="p-3">
                    <Badge variant={user.is_active ? 'success' : 'secondary'}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="p-3 text-muted-foreground">{formatDate(user.created_at)}</td>
                  <td className="p-3 text-muted-foreground" title={user.last_login_at ? formatDate(user.last_login_at) : undefined}>
                    {user.last_login_at ? formatRelativeDate(user.last_login_at) : <span className="text-muted-foreground/60">Never</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => impersonateMutation.mutate(user.id)}
                        disabled={user.is_superadmin}
                      >
                        <Eye className="h-3.5 w-3.5 mr-1" />
                        Impersonate
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (confirm('Deactivate this user?')) deactivateMutation.mutate(user.id)
                        }}
                        disabled={!user.is_active}
                      >
                        <UserX className="h-3.5 w-3.5 mr-1" />
                        Deactivate
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
